import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { listDiscountCodes, createDiscountCode, patchDiscountCode, deleteDiscountCode } from "@/lib/adminApi";
import { validateDiscount, discountPayload, apiErrorText } from "@/lib/adminHelpers";
import useNoindex from "@/lib/useNoindex";

const EMPTY = { code: "", kind: "percent", value: "", applies_to: ["report", "consulting"], valid_from: "", valid_until: "", max_uses: "", active: true, note: "" };
const PRODUCTS = { report: "Rapor", consulting: "Danışmanlık" };
const day = (iso) => (iso ? new Date(iso).toLocaleDateString("tr-TR") : "-");

export default function Discounts() {
  useNoindex();
  const [codes, setCodes] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    try { setCodes(await listDiscountCodes()); } catch (e) { toast.error(apiErrorText(e, "Kodlar yüklenemedi")); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleProduct = (p) =>
    setForm((f) => ({ ...f, applies_to: f.applies_to.includes(p) ? f.applies_to.filter((x) => x !== p) : [...f.applies_to, p] }));

  const create = async (e) => {
    e.preventDefault();
    const errs = validateDiscount(form);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      await createDiscountCode(discountPayload(form));
      toast.success("Kod oluşturuldu");
      setForm(EMPTY);
      await load();
    } catch (err) {
      toast.error(err?.response?.status === 409 ? "Bu kod zaten var" : apiErrorText(err));
    } finally { setBusy(false); }
  };

  const toggleActive = async (c) => {
    try { await patchDiscountCode(c.code, { active: !c.active }); await load(); }
    catch (e) { toast.error(apiErrorText(e)); }
  };
  const confirmDelete = async () => {
    const code = toDelete;
    setToDelete(null);
    try { await deleteDiscountCode(code); toast.success("Silindi"); await load(); }
    catch (e) { toast.error(apiErrorText(e)); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <main className="mx-auto max-w-5xl px-4 py-6">
      <h1 className="mb-4 text-xl font-semibold">İndirim kodları</h1>

      <form onSubmit={create} noValidate className="mb-8 grid gap-3 rounded-md border p-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="discount-form">
        <div className="space-y-1.5">
          <Label htmlFor="d-code">Kod</Label>
          <Input id="d-code" value={form.code} onChange={set("code")} placeholder="SOMMER10" />
          {errors.code && <p className="text-xs text-red-600">{errors.code}</p>}
        </div>
        <div className="space-y-1.5">
          <Label>Tür</Label>
          <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="percent">Yüzde (%)</SelectItem>
              <SelectItem value="amount">Tutar (EUR)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="d-value">Değer</Label>
          <Input id="d-value" inputMode="decimal" value={form.value} onChange={set("value")} />
          {errors.value && <p className="text-xs text-red-600">{errors.value}</p>}
        </div>
        <div className="space-y-1.5">
          <Label>Geçerli ürün</Label>
          <div className="flex gap-4 pt-1.5">
            {Object.entries(PRODUCTS).map(([k, v]) => (
              <label key={k} className="flex items-center gap-1.5 text-sm">
                <Checkbox checked={form.applies_to.includes(k)} onCheckedChange={() => toggleProduct(k)} /> {v}
              </label>
            ))}
          </div>
          {errors.applies_to && <p className="text-xs text-red-600">{errors.applies_to}</p>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="d-from">Başlangıç</Label>
          <Input id="d-from" type="date" value={form.valid_from} onChange={set("valid_from")} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="d-until">Bitiş</Label>
          <Input id="d-until" type="date" value={form.valid_until} onChange={set("valid_until")} />
          {errors.valid_until && <p className="text-xs text-red-600">{errors.valid_until}</p>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="d-max">Maks. kullanım</Label>
          <Input id="d-max" inputMode="numeric" value={form.max_uses} onChange={set("max_uses")} placeholder="sınırsız" />
          {errors.max_uses && <p className="text-xs text-red-600">{errors.max_uses}</p>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="d-note">Not</Label>
          <Input id="d-note" value={form.note} onChange={set("note")} maxLength={500} />
        </div>
        <div className="flex items-end gap-4">
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /> Aktif
          </label>
          <Button type="submit" disabled={busy}>Oluştur</Button>
        </div>
      </form>

      {codes === null && <p className="text-sm text-muted-foreground">Yükleniyor...</p>}
      {codes && codes.length === 0 && <p className="text-sm text-muted-foreground">Henüz kod yok.</p>}
      {codes && codes.length > 0 && (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Kod</TableHead><TableHead>İndirim</TableHead><TableHead>Ürün</TableHead>
                <TableHead>Geçerlilik</TableHead><TableHead>Kullanım</TableHead><TableHead>Aktif</TableHead><TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {codes.map((c) => (
                <TableRow key={c.code} data-testid="discount-row">
                  <TableCell className="font-mono text-sm">{c.code}{c.note && <div className="font-sans text-xs text-muted-foreground">{c.note}</div>}</TableCell>
                  <TableCell>{c.kind === "percent" ? `%${c.value}` : `${c.value} EUR`}</TableCell>
                  <TableCell className="text-xs">{(c.applies_to || []).map((p) => PRODUCTS[p] || p).join(", ")}</TableCell>
                  <TableCell className="text-xs">{day(c.valid_from)} - {day(c.valid_until)}</TableCell>
                  <TableCell>{c.uses ?? 0} / {c.max_uses ?? "∞"}</TableCell>
                  <TableCell><Switch checked={!!c.active} onCheckedChange={() => toggleActive(c)} aria-label="Aktif" /></TableCell>
                  <TableCell><Button size="sm" variant="outline" onClick={() => setToDelete(c.code)}>Sil</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kod silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>{toDelete} kalıcı olarak silinecek.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}
